import { useEffect, useState } from 'react';
import { InfoCard } from '@backstage/core-components';
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Link,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const STEPS = [
  { id: 'catalog', label: 'Browse the catalog', description: 'Explore all services, APIs, and resources', href: '/catalog' },
  { id: 'template', label: 'Run a template', description: 'Scaffold a new service or app', href: '/create' },
  { id: 'import', label: 'Connect your repo', description: 'Register an existing component', href: '/catalog-import' },
  { id: 'service', label: 'View your service', description: 'Open your owned components', href: '/catalog?filters[owned]=owned' },
];

const storageKey = (userRef: string) => `idp_getting_started_${userRef}`;

const readChecked = (userRef = 'guest'): Record<string, boolean> => {
  try {
    return JSON.parse(localStorage.getItem(storageKey(userRef)) ?? '{}');
  } catch {
    return {};
  }
};

const useStyles = makeStyles(theme => ({
  listItem: {
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  completedText: {
    textDecoration: 'line-through',
    color: theme.palette.text.secondary,
  },
  subtitle: {
    fontSize: '0.75rem',
    color: theme.palette.text.secondary,
  },
  allDone: {
    padding: theme.spacing(2),
    textAlign: 'center',
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
}));

export const GettingStartedCard = ({ userEntityRef }: { userEntityRef?: string }) => {
  const classes = useStyles();
  const [checked, setChecked] = useState<Record<string, boolean>>(() => readChecked(userEntityRef));

  useEffect(() => {
    setChecked(readChecked(userEntityRef));
  }, [userEntityRef]);

  const toggle = (id: string) => {
    const next = { ...checked, [id]: !checked[id] };
    setChecked(next);
    localStorage.setItem(storageKey(userEntityRef ?? 'guest'), JSON.stringify(next));
  };

  const allDone = STEPS.every(s => checked[s.id]);

  return (
    <InfoCard title="New here? Get started" subheader="Complete these 4 steps to hit your stride">
      {allDone ? (
        <Typography className={classes.allDone}>
          You're all set! Welcome aboard. 🎉
        </Typography>
      ) : (
        <List dense disablePadding>
          {STEPS.map((step, idx) => (
            <ListItem
              key={step.id}
              button
              className={classes.listItem}
              onClick={() => toggle(step.id)}
            >
              <ListItemIcon>
                <Checkbox
                  edge="start"
                  checked={!!checked[step.id]}
                  tabIndex={-1}
                  disableRipple
                  color="primary"
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Link href={step.href} color="inherit" underline="hover" onClick={e => e.stopPropagation()}>
                    <span className={checked[step.id] ? classes.completedText : undefined}>
                      {idx + 1}. {step.label}
                    </span>
                  </Link>
                }
                secondary={
                  <span className={classes.subtitle}>{step.description}</span>
                }
              />
            </ListItem>
          ))}
        </List>
      )}
    </InfoCard>
  );
};
